import { Listbox, Transition } from '@headlessui/react'
import { DisplayExtractor, KeyExtractor } from 'lib/type-utils'
import { FC, forwardRef, useRef, useState } from 'react'
import { c } from 'lib/component-utils'
import SelectDropdown from './SelectDropdown'
import SelectButton from './SelectButton'
import Button from './Button'
import { AnchorPropsWithSelection } from '@headlessui/react/dist/internal/floating'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/outline'

type Props<T> = {
  selected: T
  setSelected: (value: T) => void
  options: T[]
  optionKey?: KeyExtractor<T>
  display?: DisplayExtractor<T>
  optionDisplay?: DisplayExtractor<T>
  className?: string
  dropdownClassName?: string
  dropdownAnchor?: AnchorPropsWithSelection
  small?: boolean
  placeholder?: string
  label?: string
  disabled?: boolean
  button?: FC<{
    open: boolean
    selected: T
    display?: DisplayExtractor<T>
    placeholder?: string
    [key: string]: any
  }>
  showNavigation?: boolean
  searchable?: boolean
  searchFilter?: (item: T, query: string) => boolean
  searchPlaceholder?: string
  searchMinItems?: number
  ref?: React.Ref<HTMLDivElement>
}

const Select = <T,>(
  {
    selected,
    setSelected,
    options,
    optionKey,
    display,
    optionDisplay,
    className,
    dropdownClassName,
    dropdownAnchor,
    small,
    placeholder,
    label,
    disabled,
    button: SelectButtonComponent = SelectButton,
    showNavigation = false,
    searchable = false,
    searchFilter,
    searchPlaceholder,
    searchMinItems = 10,
  }: Props<T>,
  ref: React.Ref<HTMLDivElement>
) => {
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const showSearch =
    searchable && searchFilter && options.length >= searchMinItems

  const filteredOptions = showSearch
    ? options.filter((option) => searchFilter(option, searchQuery))
    : options

  const currentIndex = showNavigation
    ? options.findIndex((option) => {
        if (optionKey) {
          if (typeof optionKey === 'function') {
            return optionKey(option) === optionKey(selected)
          } else {
            return (option as any)[optionKey] === (selected as any)[optionKey]
          }
        }
        return option === selected
      })
    : -1

  const selectContent = (
    <Listbox
      value={selected}
      onChange={setSelected}
      disabled={disabled}
      by={(a, z) => {
        if (optionKey) {
          if (typeof optionKey === 'function') {
            return optionKey(a as any) === optionKey(z as any)
          } else {
            return (a as any)[optionKey] === (z as any)[optionKey]
          }
        }
        return a === z
      }}
    >
      {({ open }) => (
        <div className="relative">
          <SelectButtonComponent
            open={open}
            small={small}
            selected={selected}
            display={display}
            placeholder={placeholder}
            disabled={disabled}
            className="max-w-full min-w-full"
          />
          <Transition
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => showSearch && setSearchQuery('')}
            afterEnter={() => showSearch && searchInputRef.current?.focus()}
          >
            <SelectDropdown
              options={filteredOptions}
              display={optionDisplay ?? display}
              optionKey={optionKey}
              className={c`${dropdownClassName}`}
              anchor={dropdownAnchor}
              searchable={showSearch}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              searchPlaceholder={searchPlaceholder}
              searchInputRef={searchInputRef}
            />
          </Transition>
        </div>
      )}
    </Listbox>
  )

  return (
    <div className={c`relative ${className}`} ref={ref}>
      {label && (
        <div className="inline-block w-full mb-1 text-sm font-medium text-black">{label}</div>
      )}
      {showNavigation && options.length > 1 ? (
        <div className="flex items-stretch gap-1">
          <Button
            theme="white"
            onClick={() => currentIndex > 0 && setSelected(options[currentIndex - 1])}
            disabled={currentIndex <= 0}
            className="!px-3 flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">{selectContent}</div>
          <Button
            theme="white"
            onClick={() =>
              currentIndex < options.length - 1 && setSelected(options[currentIndex + 1])
            }
            disabled={currentIndex >= options.length - 1}
            className="!px-3 flex-shrink-0"
          >
            <ArrowRightIcon className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        selectContent
      )}
    </div>
  )
}

export default forwardRef(Select as any) as typeof Select
